import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, Trash2 } from 'lucide-react';
import type { AuditSettings as AuditSettingsType } from './MenuSettingsTypes';

interface AuditSettingsProps {
  settings: AuditSettingsType;
  onChange: (settings: AuditSettingsType) => void;
}

type AuditField = keyof AuditSettingsType;

export const AuditSettings: React.FC<AuditSettingsProps> = ({
  settings,
  onChange,
}) => {
  const [newItem, setNewItem] = useState({ type: '' as AuditField | '', value: '' });

  const handleAddItem = (field: AuditField) => {
    if (!newItem.value.trim()) return;

    const updatedSettings = {
      ...settings,
      [field]: [...settings[field], newItem.value.trim()]
    };

    onChange(updatedSettings);
    setNewItem({ ...newItem, value: '' });
  };

  const handleRemoveItem = (field: AuditField, index: number) => {
    const updatedList = [...settings[field]];
    updatedList.splice(index, 1);

    onChange({
      ...settings,
      [field]: updatedList
    });
  };

  const handleItemChange = (field: AuditField, index: number, value: string) => {
    const updatedList = [...settings[field]];
    updatedList[index] = value;

    onChange({
      ...settings,
      [field]: updatedList
    });
  };

  const renderListEditor = (
    title: string,
    field: keyof AuditSettingsType,
    placeholder: string
  ) => (
    <>
      <div className="outline outline-1 outline-border rounded-md p-4">
        <h3 className="text-md font-semibold text-foreground mb-2">{title}</h3>
        <div className="flex gap-2">
          <Input
            placeholder={placeholder}
            value={newItem.type === field ? newItem.value : ''}
            onChange={(e) => setNewItem({ type: field, value: e.target.value })}
            onKeyDown={(e) => e.key === 'Enter' && handleAddItem(field)}
            className="mb-2"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => handleAddItem(field)}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <div className="border rounded-md divide-y">
          {settings[field].map((item, index) => (
            <div key={index} className="flex items-center justify-between p-2">
              <Input
                value={item}
                onChange={(e) => handleItemChange(field, index, e.target.value)}
                className="border-0 shadow-none focus-visible:ring-0"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => handleRemoveItem(field, index)}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  return (
    <div className="space-y-6">
      <div className="outline outline-1 outline-border rounded-md p-4">
        <h3 className="text-lg font-semibold text-foreground mb-4">Audit Settings</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Configure options for audit findings and risk management features
        </p>
        
        <div className="space-y-6">
          {renderListEditor('Finding Types', 'findingTypes', 'Add new finding type')}
          {renderListEditor('Risk Categories', 'riskCategories', 'Add new risk category')}
        </div>
      </div>
    </div>
  );
};
